import React, { useEffect, useState } from "react";
import { X, ArrowsOut, PencilSimple, Check, Microphone, Trash } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { pickSupportedAudioMime, extForMime } from "@/lib/audio";

/**
 * Reusable evaluation card. Clamps comment to 2 lines; opens a modal with full
 * text + metadata on click. Coaches & head_coaches can edit rating/comment in-place.
 *
 * Props expected on `ev`:
 *   id, player_id, category_id, term_name, term_year,
 *   rating, comment, category_name, coach_name, coach_user_id,
 *   created_at, updated_at, player_name (optional, for head-coach feed)
 *
 * Props:
 *   onUpdated(updatedEv) — fired after a successful save so the parent can refresh.
 */
export default function EvalCard({ ev, showPlayer = false, dense = false, testid, onUpdated }) {
  const [open, setOpen] = useState(false);
  const comment = ev.comment || "";
  const isLong = comment.length > 110 || comment.split("\n").length > 2;

  return (
    <>
      <div
        onClick={() => setOpen(true)}
        className={`surface ${dense ? "p-3" : "p-4"} cursor-pointer hover:border-[#dfff00]/40 transition-colors group relative`}
        data-testid={testid || `eval-card-${ev.id}`}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen(true); } }}
      >
        <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
          <div className="flex items-center gap-2 min-w-0 flex-wrap">
            {showPlayer && ev.player_name && (
              <span className="font-semibold text-sm">{ev.player_name}</span>
            )}
            {showPlayer && ev.player_name && <span className="text-zinc-600 text-xs">•</span>}
            <span className="font-semibold text-sm truncate">{ev.category_name}</span>
            {ev.rating && (
              <span className="text-xs px-2 py-0.5 bg-[#dfff00] text-black font-bold rounded shrink-0">
                {ev.rating}/5
              </span>
            )}
            {ev.status === "draft" && (
              <span className="text-[10px] px-1.5 py-0.5 bg-[#e3735e]/15 text-[#e3735e] font-bold rounded uppercase tracking-wider shrink-0" data-testid={`draft-badge-${ev.id}`}>
                Draft
              </span>
            )}
          </div>
          <ArrowsOut size={14} weight="bold" className="text-zinc-600 group-hover:text-[#dfff00] transition-colors shrink-0" />
        </div>
        {comment && (
          <div className="text-sm text-zinc-300 mt-1" style={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}>
            {comment}
          </div>
        )}
        <div className="flex items-center justify-between mt-2 gap-2">
          <div className="text-xs text-zinc-500 truncate">— {ev.coach_name}</div>
          {isLong && (
            <span className="text-[10px] uppercase tracking-wider font-bold text-[#dfff00] shrink-0">Read more</span>
          )}
        </div>
      </div>

      {open && (
        <EvalModal
          ev={ev}
          showPlayer={showPlayer}
          onClose={() => setOpen(false)}
          onUpdated={onUpdated}
        />
      )}
    </>
  );
}

function EvalModal({ ev: initial, showPlayer, onClose, onUpdated }) {
  const { user } = useAuth();
  const [ev, setEv] = useState(initial);
  const [editing, setEditing] = useState(false);
  const [rating, setRating] = useState(initial.rating ?? null);
  const [comment, setComment] = useState(initial.comment ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // Voice-to-text state
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRef = React.useRef(null);
  const chunksRef = React.useRef([]);

  // Only coach roles can edit. Head coaches override anyone in their club.
  const canEdit = user && ["coach", "head_coach"].includes(user.role) &&
                  (user.role === "head_coach" || user.id === ev.coach_user_id);
  // Coaches can delete their own drafts; admins can delete anything.
  const canDelete = canEdit && (ev.status === "draft" || user.role === "head_coach");
  const dirty = editing && (rating !== (ev.rating ?? null) || (comment || "") !== (ev.comment || ""));

  const remove = async () => {
    const what = ev.status === "draft" ? "draft" : "published evaluation";
    if (!window.confirm(`Delete this ${what} (${ev.category_name || "category"})? This can't be undone.`)) return;
    setBusy(true); setErr("");
    try {
      await api.delete(`/term-evaluations/${ev.id}`);
      onUpdated?.();
      onClose();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
      setBusy(false);
    }
  };

  const termLabel = ev.term_name && ev.term_year ? `${ev.term_name} ${ev.term_year}` : null;
  const evalDateLabel = ev.evaluation_date
    ? new Date(ev.evaluation_date + "T00:00:00").toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })
    : termLabel;
  const dateText = ev.updated_at || ev.created_at;

  const startEdit = () => { setRating(ev.rating ?? null); setComment(ev.comment ?? ""); setEditing(true); setErr(""); };
  const cancelEdit = () => { setEditing(false); setErr(""); };

  const save = async () => {
    if (!ev.player_id || !ev.category_id || !ev.evaluation_date) {
      setErr("Evaluation is missing identifiers — please refresh and try again.");
      return;
    }
    setBusy(true); setErr("");
    try {
      const res = await api.post("/term-evaluations", {
        player_id: ev.player_id,
        category_id: ev.category_id,
        evaluation_date: ev.evaluation_date,
        rating: rating ?? null,
        comment: comment || "",
      });
      const updated = {
        ...ev,
        ...res.data,
        // Preserve enriched fields that the upsert response doesn't return
        category_name: ev.category_name,
        coach_name: user?.name || ev.coach_name,
        coach_user_id: res.data.coach_user_id || user?.id,
        player_name: ev.player_name,
      };
      setEv(updated);
      setEditing(false);
      onUpdated?.(updated);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  // Voice-to-text — re-uses the existing /api/transcribe Whisper endpoint
  const toggleRecord = async () => {
    if (recording) {
      mediaRef.current?.stop();
      setRecording(false);
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setErr("Voice dictation isn't available in this browser. Try Chrome or Safari.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickSupportedAudioMime();
      chunksRef.current = [];
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      mediaRef.current = rec;
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        // iOS Safari may have used audio/mp4 even if we asked for webm — use what the recorder actually produced
        const actualMime = rec.mimeType || mime || "audio/webm";
        const blob = new Blob(chunksRef.current, { type: actualMime });
        if (blob.size === 0) {
          setErr("No audio captured — check your microphone permission and try again.");
          return;
        }
        const ext = extForMime(actualMime);
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("audio", blob, `comment.${ext}`);
          const res = await api.post("/transcribe", fd);
          const txt = (res.data?.text || "").trim();
          if (!txt) {
            setErr("Couldn't hear anything — try speaking a little louder or closer to the mic.");
          } else {
            setComment((c) => (c ? c.trim() + " " : "") + txt);
          }
        } catch (e) {
          const reason = formatApiError(e.response?.data?.detail) || e.message;
          setErr(`Transcription failed: ${reason}`);
        } finally {
          setTranscribing(false);
        }
      };
      rec.start();
      setRecording(true);
    } catch (e) {
      setErr("Microphone permission denied. Tap the lock icon in the address bar to allow it.");
    }
  };

  // Stop any in-flight recording when modal closes
  useEffect(() => {
    return () => {
      if (mediaRef.current && mediaRef.current.state !== "inactive") {
        mediaRef.current.stop();
      }
    };
  }, []);

  return (
    <div
      className="fixed inset-0 bg-black/75 z-50 flex items-center justify-center p-4"
      onClick={() => { if (!busy) onClose(); }}
      data-testid="eval-modal"
    >
      <div
        className="surface w-full max-w-lg max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-5 border-b border-[#27272a] flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              {evalDateLabel && (
                <span className="px-2 py-0.5 bg-[#dfff00] text-black rounded text-xs font-bold uppercase tracking-wider">
                  {evalDateLabel}
                </span>
              )}
              {ev.status === "draft" && (
                <span className="px-2 py-0.5 bg-[#e3735e]/15 text-[#e3735e] rounded text-xs font-bold uppercase tracking-wider" data-testid="eval-modal-draft">
                  Draft
                </span>
              )}
              {!editing && ev.rating && (
                <span className="px-2 py-0.5 bg-[#dfff00]/15 text-[#dfff00] rounded text-xs font-bold" data-testid="eval-modal-rating">
                  {ev.rating}/5
                </span>
              )}
            </div>
            <h3 className="font-heading uppercase tracking-tight text-2xl mt-2 break-words">
              {ev.category_name}
            </h3>
            {showPlayer && ev.player_name && (
              <div className="text-sm text-zinc-400 mt-1">{ev.player_name}</div>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {canEdit && !editing && (
              <button onClick={startEdit} className="btn-ghost" data-testid="eval-edit-btn" title="Edit">
                <PencilSimple size={16} weight="bold" />
              </button>
            )}
            {canDelete && !editing && (
              <button onClick={remove} className="btn-ghost text-[#ff0026]" data-testid="eval-delete-btn" title="Delete evaluation" disabled={busy}>
                <Trash size={16} weight="bold" />
              </button>
            )}
            <button onClick={onClose} className="btn-ghost" data-testid="eval-modal-close" disabled={busy}>
              <X size={18} weight="bold" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {editing ? (
            <div className="space-y-4" data-testid="eval-edit-form">
              <div>
                <div className="font-label mb-2">Rating</div>
                <div className="flex items-center gap-2" data-testid="eval-rating-row">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setRating(rating === n ? null : n)}
                      className={`w-10 h-10 rounded-md font-heading text-lg transition-colors ${
                        rating != null && n <= rating
                          ? "bg-[#dfff00] text-black"
                          : "bg-[#1a1a1a] border border-[#27272a] text-zinc-500 hover:text-zinc-200"
                      }`}
                      data-testid={`eval-rating-${n}`}
                      aria-label={`Rate ${n} out of 5`}
                    >
                      {n}
                    </button>
                  ))}
                  {rating != null && (
                    <button
                      type="button"
                      onClick={() => setRating(null)}
                      className="btn-ghost text-[11px] text-zinc-500 ml-1"
                      data-testid="eval-rating-clear"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="font-label">Comment</div>
                  <button
                    type="button"
                    onClick={toggleRecord}
                    disabled={transcribing}
                    className={`btn-ghost text-xs ${recording ? "text-[#ff0026]" : ""}`}
                    data-testid="eval-mic-btn"
                  >
                    <Microphone size={14} weight={recording ? "fill" : "duotone"} />
                    {transcribing ? "Transcribing…" : recording ? "Stop" : "Voice"}
                  </button>
                </div>
                <textarea
                  className="input"
                  rows={6}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Coach notes…"
                  data-testid="eval-comment-textarea"
                />
              </div>
              {err && (
                <div className="text-sm text-[#ff0026] bg-[#ff0026]/10 border border-[#ff0026]/30 rounded-md p-2" data-testid="eval-edit-error">
                  {err}
                </div>
              )}
            </div>
          ) : ev.comment ? (
            <p className="text-zinc-200 whitespace-pre-wrap leading-relaxed" data-testid="eval-modal-comment">
              {ev.comment}
            </p>
          ) : (
            <p className="text-zinc-500 italic">No written comment for this category.</p>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-[#27272a] flex items-center justify-between text-xs text-zinc-500 gap-3 flex-wrap">
          {editing ? (
            <>
              <button onClick={cancelEdit} className="btn-ghost" disabled={busy} data-testid="eval-edit-cancel">
                Cancel
              </button>
              <button
                onClick={save}
                disabled={busy || !dirty}
                className="btn-primary"
                data-testid="eval-edit-save"
              >
                <Check size={14} weight="bold" /> {busy ? "Saving…" : "Save changes"}
              </button>
            </>
          ) : (
            <>
              <div>
                <span className="font-label">Coach</span>
                <span className="ml-2 text-zinc-300">{ev.coach_name}</span>
              </div>
              {dateText && (
                <div className="text-right">
                  <span className="font-label">Updated</span>
                  <span className="ml-2 text-zinc-400">{new Date(dateText).toLocaleString()}</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
